// Android back button closes the topmost open box instead of leaving the page
function useBackToCloseCP(open, close) {
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ mhModal: true }, '');
    const onPop = () => close();
    window.addEventListener('popstate', onPop);
    return () => {
      window.removeEventListener('popstate', onPop);
      // Closing via a button: neutralize the marker without navigating anywhere
      if (window.history.state && window.history.state.mhModal) window.history.replaceState({}, '');
    };
  }, [open]);
}

import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { B, FONT, fmtDate, fmt$ } from '../lib/constants';
import DecisionDetailModal from './modals/DecisionDetailModal';
import NeededPurchases from './NeededPurchases';
import PurchasesTab from './PurchasesTab';
import WeatherWidget from './WeatherWidget';
import ChecklistTab from './ChecklistTab';
import { usePushNotifications } from '../hooks/usePushNotifications';

const LOGO = '/logo192.png';

export default function ClientPortal({ user, project }) {
  const [tab, setTab] = useState('overview');
  const { permission, requestPermission } = usePushNotifications();

  // Deep link: notification taps carry ?tab=notes etc.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const deepTab = params.get('tab');
    if (deepTab && ['overview', 'alerts', 'decisions', 'purchases', 'financial', 'notes', 'checklist'].includes(deepTab)) {
      setTab(deepTab);
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);
  const [decisions, setDecisions] = useState([]);
  const [selectedDecision, setSelectedDecision] = useState(null);
  const [showPreBuild, setShowPreBuild] = useState(false);
  useBackToCloseCP(showPreBuild, () => setShowPreBuild(false));
  useBackToCloseCP(!!selectedDecision, () => setSelectedDecision(null));

  useEffect(() => { loadData(); }, [project?.id]);

  const loadData = async () => {
    if (!project?.id) return;
    const { data: decs } = await supabase.from('decisions').select('*').eq('project_id', project.id).order('created_at');
    if (decs) setDecisions(decs);
  };

  const pct = project?.estimate > 0 ? Math.round((project.spent / project.estimate) * 100) : 0;
  const pending = decisions.filter(d => (d.status === 'pending' && !d.is_prebuild) || d.status === 'pending_confirmation');
  const completed = decisions.filter(d => d.status === 'approved');

  const TABS = [
    { id: 'overview', label: 'Overview' },
    { id: 'alerts', label: '🔔' },
    { id: 'decisions', label: `Decisions${pending.length > 0 ? ` (${pending.length})` : ''}` },
    { id: 'purchases', label: 'Purchases' },
    { id: 'financial', label: 'Financial' },
    { id: 'notes', label: 'Notes' },
    { id: 'checklist', label: 'Checklist' },
  ];

  if (!project) return (
    <div style={{ minHeight: '100vh', background: B.black, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT }}>
      <div style={{ color: B.muted, fontSize: 13 }}>Loading your build…</div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', maxWidth: '100vw', overflowX: 'hidden', background: B.black, fontFamily: FONT, display: 'flex', flexDirection: 'column' }}>

      {/* Header */}
      <div style={{ background: '#0A0A0A', borderBottom: `1px solid #1A1A1A`, padding: '18px 20px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <img src={LOGO} alt="Muller Homes" style={{ height: 36 }} onError={e => e.target.style.display='none'} />
          <button onClick={() => supabase.auth.signOut()}
            style={{ background: 'transparent', border: `1px solid #2A2A2A`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: B.muted, fontFamily: FONT, fontSize: 12 }}>
            Sign Out
          </button>
        </div>
        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#6F6F6F', fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4 }}>Your Build</div>
          <div style={{ color: '#FFFFFF', fontSize: 22, fontWeight: 800 }}>{project.name}</div>
          <div style={{ color: '#6F6F6F', fontSize: 12, marginTop: 2 }}>{project.address}</div>
        </div>
        <div style={{ display: 'flex', gap: 0, overflowX: 'auto', scrollbarWidth: 'none', marginLeft: -20, paddingLeft: 20 }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              style={{ background: 'transparent', border: 'none', borderBottom: tab === t.id ? `2px solid ${B.goldlt}` : '2px solid transparent', color: tab === t.id ? B.goldlt : '#555', padding: '10px 16px', cursor: 'pointer', fontSize: 12, fontWeight: tab === t.id ? 700 : 400, textTransform: 'uppercase', letterSpacing: 1, fontFamily: FONT, whiteSpace: 'nowrap', flexShrink: 0 }}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', padding: 16, maxWidth: '100%', boxSizing: 'border-box' }}>

        {permission !== 'granted' && (
          <div onClick={requestPermission}
            style={{ background: 'rgba(248,218,107,0.1)', border: '1px solid #9A732F', borderRadius: 10, padding: '10px 16px', marginBottom: 16, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 16 }}>🔔</span>
            <span style={{ color: '#F8DA6B', fontSize: 13, fontWeight: 700 }}>Tap to enable notifications</span>
            <span style={{ color: '#6F6F6F', fontSize: 11, marginLeft: 'auto' }}>never miss a decision</span>
          </div>
        )}

        {/* OVERVIEW */}
        {tab === 'overview' && (
          <div>
            {/* Progress card */}
            <div style={{ background: 'linear-gradient(135deg,#1A1400 0%,#0F0F0F 60%)', border: `1px solid ${B.gold}`, borderRadius: 16, padding: 24, marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
              <div style={{ position: 'absolute', top: -30, right: -30, width: 140, height: 140, borderRadius: '50%', background: 'radial-gradient(circle,rgba(154,115,47,0.15) 0%,transparent 70%)' }} />
              <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 10 }}>Build Progress</div>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, marginBottom: 16 }}>
                <div style={{ fontSize: 60, fontWeight: 900, lineHeight: 1, background: `linear-gradient(135deg,${B.goldlt},${B.gold})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                  {project.progress}%
                </div>
                <div style={{ paddingBottom: 6 }}>
                  <div style={{ color: '#E8E4DC', fontSize: 16, fontWeight: 700 }}>{project.phase}</div>
                  <div style={{ color: '#555', fontSize: 12 }}>Current Phase</div>
                </div>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.06)', borderRadius: 999, height: 6 }}>
                <div style={{ height: '100%', width: `${project.progress}%`, background: `linear-gradient(90deg,${B.goldlt},${B.gold})`, borderRadius: 999 }} />
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 14 }}>
                <div>
                  <div style={{ color: '#555', fontSize: 10, letterSpacing: 1 }}>STARTED</div>
                  <div style={{ color: '#E8E4DC', fontSize: 12 }}>{fmtDate(project.start_date)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ color: '#555', fontSize: 10, letterSpacing: 1 }}>EST. COMPLETE</div>
                  <div style={{ color: '#E8E4DC', fontSize: 12 }}>{fmtDate(project.est_complete)}</div>
                </div>
              </div>
            </div>

            {/* Weather at the build site */}
            <WeatherWidget address={project.address} projectId={project.id} />

            {/* Purchases needed widget */}
            <NeededPurchases projectId={project.id} onOpenPurchases={() => setTab('purchases')} />

            <PreBuildBanner decisions={decisions} onStart={() => setShowPreBuild(true)} />

            {/* Pending decisions preview */}
            {pending.length > 0 && (
              <div onClick={() => setTab('decisions')}
                style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 14, border: `1px solid ${B.gold}`, cursor: 'pointer' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase' }}>Decisions Needed</div>
                  <div style={{ background: B.goldlt, color: B.black, borderRadius: 10, padding: '2px 8px', fontSize: 11, fontWeight: 700 }}>{pending.length}</div>
                </div>
                {pending.slice(0, 3).map(d => (
                  <div key={d.id} style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: 6, marginBottom: 6, borderBottom: `1px solid ${B.slate}` }}>
                    <div style={{ color: B.white, fontSize: 13 }}>{d.item}</div>
                    {d.due_date && <div style={{ color: B.muted, fontSize: 11 }}>{fmtDate(d.due_date)}</div>}
                  </div>
                ))}
                {pending.length > 3 && <div style={{ color: B.muted, fontSize: 11 }}>+{pending.length - 3} more</div>}
              </div>
            )}
          </div>
        )}

        {/* DECISIONS */}
        {tab === 'alerts' && (
          <ClientNotificationsTab project={project} />
        )}

        {tab === 'alerts' && (
          <ClientNotifications projectId={project.id} />
        )}

        {tab === 'decisions' && (
          <div>
            <PreBuildBanner decisions={decisions} onStart={() => setShowPreBuild(true)} />
            {pending.length === 0 && completed.length === 0 && (
              <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
                <div style={{ fontSize: 32, marginBottom: 10 }}>✅</div>
                <div style={{ color: B.muted, fontSize: 13 }}>No decisions right now.</div>
              </div>
            )}
            {pending.length > 0 && (
              <div style={{ marginBottom: 24 }}>
                <div style={{ color: B.muted, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Needs Your Input · {pending.length}</div>
                {pending.map(d => (
                  <button key={d.id} onClick={() => setSelectedDecision(d)}
                    style={{ width: '100%', background: '#0F0F0F', borderRadius: 12, padding: '16px', marginBottom: 10, border: `1px solid ${B.gold}`, borderLeft: `3px solid ${B.goldlt}`, cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}>
                    <div style={{ color: B.white, fontSize: 14, fontWeight: 700, marginBottom: 4 }}>{d.item}</div>
                    {d.category && <div style={{ color: B.goldlt, fontSize: 12, marginBottom: 4 }}>{d.category}</div>}
                    {d.notes && <div style={{ color: '#555', fontSize: 12, lineHeight: 1.4 }}>{d.notes}</div>}
                    {d.due_date && <div style={{ color: '#555', fontSize: 11, marginTop: 6 }}>Needed by {fmtDate(d.due_date)}</div>}
                  </button>
                ))}
              </div>
            )}
            {completed.length > 0 && (
              <div>
                <div style={{ color: B.muted, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 12 }}>Completed · {completed.length}</div>
                {completed.map(d => {
                  const parent = d.parent_decision_id ? decisions.find(x => x.id === d.parent_decision_id) : null;
                  return (
                  <button key={d.id} onClick={() => setSelectedDecision(d)}
                    style={{ width: '100%', background: '#0F0F0F', borderRadius: 12, padding: '14px 16px', marginBottom: 8, border: '1px solid #1A1A1A', borderLeft: '3px solid #4CAF7D', cursor: 'pointer', textAlign: 'left', fontFamily: FONT }}>
                    {parent && <div style={{ color: '#4FA3E0', fontSize: 11, fontWeight: 700, marginBottom: 2 }}>{parent.item}{d.parent_option ? ` · ${d.parent_option}` : ''}</div>}
                    <div style={{ color: B.white, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{parent ? '↳ ' : ''}{d.item}</div>
                    {d.answer && <div style={{ color: '#4CAF7D', fontSize: 13 }}>{d.answer}</div>}
                    <div style={{ color: '#555', fontSize: 11, marginTop: 4 }}>
                      {d.completed_by || 'Completed'} · {d.completed_at ? fmtDate(d.completed_at.split('T')[0]) : ''}
                    </div>
                  </button>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PURCHASES */}
        {tab === 'purchases' && (
          <PurchasesTab project={project} isBuilder={false} />
        )}

        {/* FINANCIAL */}
        {tab === 'financial' && (
          <ClientFinancial project={project} />
        )}

        {/* NOTES */}
        {tab === 'notes' && (
          <ClientNotes project={project} user={user} />
        )}

        {/* CHECKLIST */}
        {tab === 'checklist' && (
          <ChecklistTab project={project} isBuilder={false} />
        )}

      </div>

      {showPreBuild && (
        <PreBuildWizard project={project} user={user}
          onClose={() => { setShowPreBuild(false); loadData(); }}
          onDone={() => { setShowPreBuild(false); loadData(); }} />
      )}

      {selectedDecision && (
        <DecisionDetailModal
          decision={selectedDecision}
          isBuilder={false}
          onClose={() => setSelectedDecision(null)}
          onSuccess={() => { setSelectedDecision(null); loadData(); }}
        />
      )}
    </div>
  );
}

function ClientFinancial({ project }) {
  const [view, setView] = useState('summary');
  const [lines, setLines] = useState([]);
  const [exps, setExps] = useState([]);

  useEffect(() => {
    supabase.from('project_financial_lines').select('*')
      .eq('project_id', project.id).order('sort')
      .then(({ data }) => { if (data) setLines(data); });
    supabase.from('project_financial_expenses').select('*')
      .eq('project_id', project.id).order('sort')
      .then(({ data }) => { if (data) setExps(data); });
  }, [project.id]);

  const estimate = parseFloat(project.estimate) || lines.reduce((s, l) => s + (parseFloat(l.updated) || parseFloat(l.estimate) || 0), 0);
  const spent = parseFloat(project.spent) || lines.reduce((s, l) => s + (parseFloat(l.actual) || 0), 0);
  const remaining = estimate - spent;
  const pct = estimate > 0 ? Math.round((spent / estimate) * 100) : 0;
  const status = pct <= 70 ? { label: 'On track', color: '#4CAF7D' } : pct <= 90 ? { label: 'Approaching limit', color: B.goldlt } : { label: 'Over budget risk', color: '#E74C3C' };
  const money = (n) => '$' + Math.round(n).toLocaleString();

  if (!estimate && lines.length === 0) {
    return (
      <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
        <div style={{ fontSize: 32, marginBottom: 10 }}>💰</div>
        <div style={{ color: B.muted, fontSize: 13, lineHeight: 1.6 }}>Budget figures haven't been set up yet.<br/>Check back soon.</div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ color: B.white, fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Financial Summary</div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { v: 'summary', label: 'Summary' },
          { v: 'budget', label: `Budget (${lines.length})` },
          { v: 'expenses', label: `Expenses (${exps.length})` },
        ].map(opt => (
          <button key={opt.v} onClick={() => setView(opt.v)}
            style={{ flex: 1, background: view === opt.v ? 'rgba(248,218,107,0.1)' : 'transparent', border: `1px solid ${view === opt.v ? B.gold : B.slate}`, borderRadius: 9, padding: '9px 4px', cursor: 'pointer', color: view === opt.v ? B.goldlt : B.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
            {opt.label}
          </button>
        ))}
      </div>

      {view === 'summary' && (
        <>
          <div style={{ background: 'linear-gradient(135deg, #1A1400 0%, #0F0F0F 60%)', border: `1px solid ${B.gold}`, borderRadius: 16, padding: 20, marginBottom: 12 }}>
            <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 4, textTransform: 'uppercase', marginBottom: 10 }}>Budget Consumed</div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 14, marginBottom: 12 }}>
              <div style={{ fontSize: 56, fontWeight: 900, lineHeight: 1, background: `linear-gradient(135deg, ${B.goldlt}, ${B.gold})`, WebkitBackgroundClip: 'text', backgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{pct}%</div>
              <div style={{ paddingBottom: 6 }}>
                <div style={{ color: status.color, fontSize: 14, fontWeight: 700 }}>{status.label}</div>
                <div style={{ color: '#555', fontSize: 12 }}>{money(spent)} of {money(estimate)}</div>
              </div>
            </div>
            <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 999, height: 8, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${Math.min(pct, 100)}%`, background: pct > 90 ? '#E74C3C' : `linear-gradient(90deg, ${B.goldlt}, ${B.gold})`, borderRadius: 999 }} />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 14 }}>
            {[
              { label: 'Estimate', value: money(estimate), color: B.white },
              { label: 'Spent', value: money(spent), color: B.goldlt },
              { label: 'Remaining', value: money(remaining), color: remaining >= 0 ? '#4CAF7D' : '#E74C3C' },
              { label: 'Status', value: status.label, color: status.color },
            ].map(card => (
              <div key={card.label} style={{ background: '#101218', border: '1px solid #23262F', borderRadius: 12, padding: '14px 16px' }}>
                <div style={{ color: '#6F6F6F', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 6 }}>{card.label}</div>
                <div style={{ color: card.color, fontSize: 18, fontWeight: 800 }}>{card.value}</div>
              </div>
            ))}
          </div>
        </>
      )}

      {view === 'budget' && (
        lines.length === 0 ? (
          <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: 24 }}>Line items will appear after the next budget sync.</div>
        ) : lines.map(l => {
          const est = parseFloat(l.updated) || parseFloat(l.estimate) || 0;
          const act = parseFloat(l.actual) || 0;
          const over = act > est && est > 0;
          return (
            <div key={l.id} style={{ background: '#0F0F0F', borderRadius: 11, padding: '12px 14px', marginBottom: 6, border: '1px solid #1A1A1A', borderLeft: `3px solid ${over ? '#E74C3C' : act >= est && est > 0 ? '#4CAF7D' : B.slate}` }}>
              <div style={{ color: B.white, fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{l.category}</div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <div style={{ color: '#6F6F6F', fontSize: 11 }}>Budget: <span style={{ color: B.light }}>{money(est)}</span></div>
                <div style={{ color: '#6F6F6F', fontSize: 11 }}>Actual: <span style={{ color: over ? '#E74C3C' : B.goldlt }}>{money(act)}</span></div>
              </div>
            </div>
          );
        })
      )}

      {view === 'expenses' && (
        exps.length === 0 ? (
          <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: 24 }}>Expenses will appear after the next sync.</div>
        ) : exps.map(e => (
          <div key={e.id} style={{ background: '#0F0F0F', borderRadius: 11, padding: '12px 14px', marginBottom: 6, border: '1px solid #1A1A1A' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 3 }}>
              <div style={{ color: B.white, fontSize: 13, fontWeight: 700 }}>{e.vendor || 'Expense'}</div>
              <div style={{ color: B.goldlt, fontSize: 14, fontWeight: 800 }}>{money(parseFloat(e.amount) || 0)}</div>
            </div>
            {e.description && <div style={{ color: '#888', fontSize: 12, lineHeight: 1.4, marginBottom: 3 }}>{e.description}</div>}
            <div style={{ display: 'flex', gap: 10, color: '#555', fontSize: 10 }}>
              {e.expense_date && <span>{e.expense_date}</span>}
              {e.category && <span>· {e.category}</span>}
              {e.paid_by && <span>· Paid by {e.paid_by}</span>}
            </div>
          </div>
        ))
      )}

      <div style={{ color: '#6F6F6F', fontSize: 11, textAlign: 'center', lineHeight: 1.6, marginTop: 14 }}>
        Numbers update as expenses are recorded. For receipts or details,<br/>reach out to Dan directly.
      </div>
    </div>
  );
}

function ClientNotes({ project, user }) {
  const [notes, setNotes] = useState([]);
  const [view, setView] = useState('active');
  const [loading, setLoading] = useState(true);
  const [composing, setComposing] = useState(false);
  useBackToCloseCP(composing, () => setComposing(false));
  const [noteType, setNoteType] = useState('note'); // 'note' | 'question'
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await supabase.from('client_notes').select('*')
      .eq('project_id', project.id).order('created_at', { ascending: false });
    if (data) setNotes(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, [project.id]);

  const handlePost = async () => {
    if (!text.trim()) return;
    setSaving(true);
    await supabase.from('client_notes').insert({
      project_id: parseInt(project.id),
      author_id: user?.id || null,
      author_role: 'client',
      type: noteType,
      content: text.trim(),
      status: noteType === 'question' ? 'open' : null,
    });
    // Notify the builder
    try {
      const { notifyBuilder } = await import('../hooks/usePushNotifications');
      await notifyBuilder(
        noteType === 'question' ? `❓ Question — ${project.name}` : `📝 New Note — ${project.name}`,
        text.trim().slice(0, 100),
        `${window.location.origin}?project=${project.id}&tab=notes`
      );
    } catch(e) {}
    setText('');
    setComposing(false);
    setSaving(false);
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Notes & Questions</div>
        <button onClick={() => setComposing(!composing)}
          style={{ background: B.goldlt, color: B.black, border: 'none', borderRadius: 9, padding: '8px 16px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>
          {composing ? '× Cancel' : '+ New'}
        </button>
      </div>

      {composing && (
        <div style={{ background: B.navy, borderRadius: 14, padding: 18, marginBottom: 16, border: `1px solid ${B.gold}` }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[{ v: 'note', label: '📝 Note' }, { v: 'question', label: '❓ Question' }].map(opt => (
              <button key={opt.v} onClick={() => setNoteType(opt.v)}
                style={{ flex: 1, background: noteType === opt.v ? 'rgba(248,218,107,0.1)' : B.mid, border: `1px solid ${noteType === opt.v ? B.gold : B.slate}`, borderRadius: 8, padding: '10px', cursor: 'pointer', color: noteType === opt.v ? B.goldlt : B.muted, fontSize: 13, fontWeight: 700, fontFamily: FONT }}>
                {opt.label}
              </button>
            ))}
          </div>
          <textarea
            value={text}
            onChange={e => setText(e.target.value)}
            autoFocus
            rows={4}
            placeholder={noteType === 'question' ? 'Ask Dan a question about your build…' : 'Leave a note about your build…'}
            style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 10, padding: '12px 14px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical', lineHeight: 1.5, marginBottom: 12 }}
          />
          <button onClick={handlePost} disabled={saving || !text.trim()}
            style={{ width: '100%', background: text.trim() ? B.goldlt : B.slate, color: text.trim() ? B.black : B.muted, border: 'none', borderRadius: 11, padding: 13, cursor: text.trim() ? 'pointer' : 'default', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
            {saving ? 'Posting…' : noteType === 'question' ? 'Send Question' : 'Post Note'}
          </button>
        </div>
      )}

      {loading ? (
        <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
      ) : notes.length === 0 && !composing ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>📝</div>
          <div style={{ color: B.muted, fontSize: 13, lineHeight: 1.6 }}>No notes yet.<br/>Leave a note or ask Dan a question anytime.</div>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {[
              { v: 'active', label: `Active (${notes.filter(n => !n.reply).length})` },
              { v: 'completed', label: `✓ Completed (${notes.filter(n => n.reply).length})` },
            ].map(opt => (
              <button key={opt.v} onClick={() => setView(opt.v)}
                style={{ flex: 1, background: view === opt.v ? 'rgba(248,218,107,0.1)' : 'transparent', border: `1px solid ${view === opt.v ? B.gold : B.slate}`, borderRadius: 9, padding: '9px', cursor: 'pointer', color: view === opt.v ? B.goldlt : B.muted, fontSize: 12, fontWeight: 700, fontFamily: FONT }}>
                {opt.label}
              </button>
            ))}
          </div>
          {(view === 'active' ? notes.filter(n => !n.reply) : notes.filter(n => n.reply)).length === 0 ? (
            <div style={{ color: B.muted, fontSize: 12, textAlign: 'center', padding: 24 }}>
              {view === 'active' ? 'Nothing waiting — all caught up.' : 'Nothing completed yet.'}
            </div>
          ) : (view === 'active' ? notes.filter(n => !n.reply) : notes.filter(n => n.reply)).map(n => (
            <NoteCard key={n.id} n={n} completed={!!n.reply} />
          ))}
        </>
      )}
    </div>
  );
}

function NoteCard({ n, completed = false }) {
  return (
    <div style={{ background: '#0F0F0F', borderRadius: 12, padding: 16, marginBottom: 10, border: `1px solid ${!completed && n.type === 'question' ? B.gold : '#1A1A1A'}`, borderLeft: `3px solid ${completed ? '#4CAF7D' : (n.type === 'question' ? B.goldlt : B.slate)}`, opacity: completed ? 0.85 : 1 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ color: completed ? '#4CAF7D' : (n.type === 'question' ? B.goldlt : B.muted), fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase' }}>
          {n.type === 'question' ? (completed ? '✓ Answered' : '❓ Question') : (completed ? '✓ Responded' : '📝 Note')}
        </div>
        <div style={{ color: '#555', fontSize: 11 }}>{fmtDate(n.created_at?.split('T')[0])}</div>
      </div>
      <div style={{ color: B.white, fontSize: 14, lineHeight: 1.5 }}>{n.content}</div>
      {n.reply && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: `1px solid ${B.slate}` }}>
          <div style={{ color: '#4CAF7D', fontSize: 10, fontWeight: 700, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>{n.type === 'question' ? "Dan's Answer" : "Dan's Response"}</div>
          <div style={{ color: '#E8E4DC', fontSize: 14, lineHeight: 1.5 }}>{n.reply}</div>
        </div>
      )}
      {n.type === 'question' && !n.reply && (
        <div style={{ color: '#555', fontSize: 11, marginTop: 8, fontStyle: 'italic' }}>Waiting for answer…</div>
      )}
    </div>
  );
}

function ClientNotificationsTab({ project }) {
  const isBuilder = false;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase.from('app_notifications').select('*')
      .eq('project_id', parseInt(project.id))
      .eq('recipient_role', isBuilder ? 'builder' : 'client')
      .eq('read', false)
      .order('created_at', { ascending: false });
    if (data) setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [project.id]);

  const markRead = async (id) => {
    await supabase.from('app_notifications').update({ read: true }).eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  const markAllRead = async () => {
    await supabase.from('app_notifications').update({ read: true })
      .eq('project_id', parseInt(project.id))
      .eq('recipient_role', isBuilder ? 'builder' : 'client')
      .eq('read', false);
    setItems([]);
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div style={{ color: B.white, fontSize: 18, fontWeight: 700 }}>Notifications</div>
        {items.length > 1 && (
          <button onClick={markAllRead}
            style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: B.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
            ✓ Mark all read
          </button>
        )}
      </div>
      {loading ? (
        <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔔</div>
          <div style={{ color: B.muted, fontSize: 13 }}>All caught up — nothing unread.</div>
        </div>
      ) : items.map(n => (
        <div key={n.id} style={{ background: B.navy, borderRadius: 12, padding: '13px 15px', marginBottom: 8, border: `1px solid ${B.slate}`, borderLeft: `3px solid ${B.goldlt}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 3 }}>{n.title}</div>
              {n.body && <div style={{ color: B.light, fontSize: 12, lineHeight: 1.5, marginBottom: 4 }}>{n.body}</div>}
              <div style={{ color: '#555', fontSize: 10 }}>
                {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(n.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            </div>
            <button onClick={() => markRead(n.id)}
              style={{ background: 'rgba(76,175,61,0.12)', border: '1px solid #4CAF7D', borderRadius: 8, padding: '7px 12px', cursor: 'pointer', color: '#4CAF7D', fontSize: 11, fontWeight: 700, fontFamily: FONT, flexShrink: 0 }}>
              ✓ Read
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


function ClientNotifications({ projectId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase.from('app_notifications').select('*')
      .eq('project_id', parseInt(projectId)).eq('recipient_role', 'client').eq('read', false)
      .order('created_at', { ascending: false }).limit(100);
    if (data) setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, [projectId]);

  const markRead = async (id) => {
    await supabase.from('app_notifications').update({ read: true }).eq('id', id);
    setItems(prev => prev.filter(i => i.id !== id));
  };

  return (
    <div>
      <div style={{ color: B.white, fontSize: 18, fontWeight: 700, marginBottom: 14 }}>Notifications{items.length > 0 ? ` (${items.length})` : ''}</div>
      {loading ? (
        <div style={{ color: B.muted, textAlign: 'center', padding: 32 }}>Loading…</div>
      ) : items.length === 0 ? (
        <div style={{ background: B.navy, borderRadius: 14, padding: 36, textAlign: 'center', border: `1px solid ${B.slate}` }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🔔</div>
          <div style={{ color: B.muted, fontSize: 13 }}>You're all caught up.</div>
        </div>
      ) : items.map(n => (
        <div key={n.id} style={{ background: B.navy, borderRadius: 12, padding: '13px 15px', marginBottom: 8, border: `1px solid ${B.slate}`, borderLeft: `3px solid ${B.goldlt}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0, cursor: n.url ? 'pointer' : 'default' }}
              onClick={() => { if (n.url) { markRead(n.id); setTimeout(() => { window.location.href = n.url; }, 120); } }}>
              <div style={{ color: B.white, fontSize: 13, fontWeight: 700, marginBottom: 2 }}>{n.title}</div>
              {n.body && <div style={{ color: '#E8E4DC', fontSize: 12, lineHeight: 1.5 }}>{n.body}</div>}
              <div style={{ color: '#555', fontSize: 10, marginTop: 4 }}>
                {new Date(n.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} {new Date(n.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
              </div>
            </div>
            <button onClick={() => markRead(n.id)}
              style={{ background: 'rgba(76,175,61,0.1)', border: '1px solid #4CAF7D', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', color: '#4CAF7D', fontSize: 11, fontWeight: 700, fontFamily: FONT, flexShrink: 0 }}>
              ✓ Read
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}


function PreBuildBanner({ decisions, onStart }) {
  const all = decisions.filter(d => d.is_prebuild && !d.parent_decision_id);
  const qs = all.filter(d => d.status === 'pending');
  if (all.length === 0) return null;
  const drafted = qs.filter(d => d.draft_answer).length;
  return (
    <div onClick={onStart}
      style={{ background: 'linear-gradient(135deg,#1A1400 0%,#0F0F0F 70%)', border: `1px solid ${B.goldlt}`, borderRadius: 14, padding: 18, marginBottom: 14, cursor: 'pointer' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ color: B.goldlt, fontSize: 10, letterSpacing: 3, textTransform: 'uppercase', marginBottom: 4 }}>📋 Pre-Build Decisions</div>
          <div style={{ color: B.white, fontSize: 14, fontWeight: 700 }}>
            {qs.length > 0 ? `${qs.length} question${qs.length !== 1 ? 's' : ''} to answer${drafted > 0 ? ` · ${drafted} drafted` : ''}` : 'All answered — view your decisions'}
          </div>
          <div style={{ color: B.muted, fontSize: 11, marginTop: 2 }}>{qs.length > 0 ? 'Organized by category — answer a few at a time, come back anytime' : 'Open to review or change answers'}</div>
        </div>
        <div style={{ color: B.goldlt, fontSize: 22 }}>→</div>
      </div>
    </div>
  );
}

function PreBuildWizard({ project, user, onClose, onDone }) {
  const CATEGORIES = [
    'Exterior', 'Interior', 'Foundation', 'Concrete', 'Framing', 'Fireplaces', 'Windows/Doors',
    'Plumbing', 'Electrical', 'HVAC', 'Tile', 'Flooring', 'Trim', 'Paint/Stain', 'Appliances', 'Finishes',
  ];
  const [questions, setQuestions] = useState([]);
  const [pageIdx, setPageIdx] = useState(0);
  const [textVals, setTextVals] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [locked, setLocked] = useState(!!project.prebuild_locked);
  const [saving, setSaving] = useState(false);
  const [justFinished, setJustFinished] = useState(false);
  const [myName, setMyName] = useState('');

  useEffect(() => {
    (async () => {
      const { data } = await supabase.from('decisions').select('*')
        .eq('project_id', project.id).eq('is_prebuild', true)
        .order('prebuild_sort', { ascending: true });
      if (data) {
        setQuestions(data);
        const t = {};
        data.forEach(q => { t[q.id] = q.draft_answer || ''; });
        setTextVals(t);
      }
      try {
        const { data: proj } = await supabase.from('projects').select('prebuild_locked').eq('id', project.id).single();
        setLocked(!!proj?.prebuild_locked);
      } catch(e) {}
      try {
        const { data: prof } = await supabase.from('user_profiles').select('*').eq('id', user?.id).single();
        setMyName(prof?.name || prof?.full_name || user?.email?.split('@')[0] || 'Client');
      } catch(e) { setMyName(user?.email?.split('@')[0] || 'Client'); }
    })();
  }, [project.id]);

  const tops = questions.filter(q => !q.parent_decision_id);
  const catRank = (cc) => { const i = CATEGORIES.indexOf(cc); return i === -1 ? 999 : i; };
  const cats = [...new Set(tops.map(q => q.category || 'Other'))].sort((a, b) => catRank(a) - catRank(b));
  const cat = cats[pageIdx] || null;
  const pageQs = tops.filter(q => (q.category || 'Other') === cat);

  const answered = tops.filter(q => q.status !== 'pending' || q.draft_answer);
  const drafts = questions.filter(q => q.status === 'pending' && q.draft_answer);
  const pct = tops.length ? Math.round((answered.length / tops.length) * 100) : 0;

  const childFor = (q, val) => (val || '').trim()
    ? questions.find(x => x.parent_decision_id === q.id && x.parent_option === (val || '').trim())
    : null;

  const missingChildren = questions.filter(q => {
    if (q.parent_decision_id || q.status !== 'pending' || !q.draft_answer) return false;
    const ch = childFor(q, q.draft_answer);
    return ch && ch.status === 'pending' && !ch.draft_answer;
  }).length;

  const patchLocal = (id, patch) => setQuestions(prev => prev.map(x => x.id === id ? { ...x, ...patch } : x));

  const saveDraft = async (q, val) => {
    if (locked) return;
    const v = (val || '').trim();
    if (!v) return;
    await supabase.from('decisions').update({ draft_answer: v }).eq('id', q.id);
    patchLocal(q.id, { draft_answer: v });
    // changing a branching pick clears stale branch drafts
    if (!q.parent_decision_id) {
      const others = questions.filter(x => x.parent_decision_id === q.id && x.parent_option !== v && x.draft_answer && x.status === 'pending');
      for (const o of others) {
        await supabase.from('decisions').update({ draft_answer: null }).eq('id', o.id);
        patchLocal(o.id, { draft_answer: null });
        setTextVals(prev => ({ ...prev, [o.id]: '' }));
      }
    }
  };

  const updateApproved = async (q, val) => {
    if (locked) return;
    const v = (val || '').trim();
    if (!v || v === q.answer) { setEditingId(null); return; }
    await supabase.from('decisions').update({
      answer: v,
      decided_by_name: myName,
      confirmed_by_name: myName,
      confirmed_at: new Date().toISOString(),
    }).eq('id', q.id);
    patchLocal(q.id, { answer: v, confirmed_by_name: myName, confirmed_at: new Date().toISOString() });
    setEditingId(null);
    try {
      const { notifyBuilder } = await import('../hooks/usePushNotifications');
      await notifyBuilder(
        `✏️ Decision changed: ${q.item}`,
        `${myName} changed it to "${v}" — ${project.name}`,
        `${window.location.origin}?project=${project.id}&tab=decisions`
      );
    } catch(e) {}
  };

  const markNA = (q) => saveDraft(q, 'Not Applicable');

  const confirmAll = async () => {
    if (locked || missingChildren > 0) return;
    setSaving(true);
    const toCommit = questions.filter(q => q.status === 'pending' && q.draft_answer);
    for (const q of toCommit) {
      await supabase.from('decisions').update({
        status: 'approved',
        answer: q.draft_answer,
        draft_answer: null,
        completed_by: 'client',
        completed_at: new Date().toISOString(),
        decided_by_name: myName,
        confirmed_by_name: myName,
        confirmed_at: new Date().toISOString(),
      }).eq('id', q.id);
    }
    const committedParents = toCommit.filter(q => !q.parent_decision_id);
    for (const p of committedParents) {
      const losers = questions.filter(ch => ch.parent_decision_id === p.id && ch.parent_option !== p.draft_answer && !ch.draft_answer && ch.status === 'pending');
      for (const ch of losers) {
        await supabase.from('decisions').update({
          status: 'approved', answer: 'N/A', completed_by: 'client',
          completed_at: new Date().toISOString(),
          decided_by_name: myName, confirmed_by_name: myName, confirmed_at: new Date().toISOString(),
        }).eq('id', ch.id);
      }
    }
    try {
      const { notifyBuilder } = await import('../hooks/usePushNotifications');
      await notifyBuilder(
        `📋 Pre-Build: ${toCommit.length} decision${toCommit.length !== 1 ? 's' : ''} completed`,
        `${myName} — ${project.name}`,
        `${window.location.origin}?project=${project.id}&tab=decisions`
      );
    } catch(e) {}
    setSaving(false);
    // reflect commits locally, advance to the next category — never auto-close
    setQuestions(prev => prev.map(x => x.draft_answer ? { ...x, status: 'approved', answer: x.draft_answer, draft_answer: null, confirmed_by_name: myName, confirmed_at: new Date().toISOString() } : x));
    if (pageIdx < cats.length - 1) {
      setPageIdx(pageIdx + 1);
      window.scrollTo(0, 0);
    } else {
      setJustFinished(true);
    }
  };

  const fmtStamp = (q) => {
    if (!q.confirmed_at) return q.confirmed_by_name || '';
    const d = new Date(q.confirmed_at);
    return `${q.confirmed_by_name || 'Client'} · ${d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  };

  const costLabel = (q, opt, active) => (q.option_costs && q.option_costs[opt] != null)
    ? <span style={{ color: active ? B.goldlt : '#8A8A8A', fontSize: 12, marginLeft: 8 }}>· ~${Number(q.option_costs[opt]).toLocaleString()}</span>
    : null;

  const renderEditor = (q, isChild) => {
    const val = q.status === 'pending' ? (q.draft_answer || '') : (q.answer || '');
    const hasOpts = Array.isArray(q.options) && q.options.length > 0;
    const commit = (v) => q.status === 'pending' ? saveDraft(q, v) : updateApproved(q, v);
    return (
      <div style={{ marginTop: 10 }}>
        {hasOpts ? (
          <div>
            {q.options.map(opt => {
              const active = (q.status === 'pending' ? (textVals['sel_' + q.id] ?? val) : (textVals['sel_' + q.id] ?? val)) === opt;
              return (
                <button key={opt} onClick={() => { setTextVals(prev => ({ ...prev, ['sel_' + q.id]: opt })); commit(opt); }}
                  style={{ display: 'block', width: '100%', textAlign: 'left', background: active ? 'rgba(248,218,107,0.12)' : B.mid, border: `1px solid ${active ? B.goldlt : B.slate}`, borderRadius: 11, padding: isChild ? '12px 14px' : '13px 15px', marginBottom: 7, cursor: 'pointer', color: active ? B.goldlt : B.white, fontSize: isChild ? 13.5 : 14.5, fontWeight: active ? 800 : 500, fontFamily: FONT }}>
                  {active ? '✓ ' : ''}{opt}{costLabel(q, opt, active)}
                </button>
              );
            })}
            <input value={(() => { const t = textVals['custom_' + q.id]; if (t != null) return t; return hasOpts && q.options.includes(val) ? '' : val; })()}
              onChange={e => setTextVals(prev => ({ ...prev, ['custom_' + q.id]: e.target.value }))}
              onBlur={e => { if (e.target.value.trim()) { setTextVals(prev => ({ ...prev, ['sel_' + q.id]: e.target.value.trim() })); commit(e.target.value); } }}
              placeholder="Or type your own…"
              style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 10, padding: '11px 13px', color: B.white, fontSize: 13, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginTop: 4 }} />
          </div>
        ) : (
          <div>
            <textarea value={textVals[q.id] ?? val} rows={3}
              onChange={e => setTextVals(prev => ({ ...prev, [q.id]: e.target.value }))}
              placeholder="Type your answer…"
              style={{ width: '100%', background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 11, padding: '12px', color: B.white, fontSize: 14, outline: 'none', fontFamily: FONT, boxSizing: 'border-box', resize: 'vertical' }} />
            <button onClick={() => commit(textVals[q.id] ?? val)}
              disabled={!(textVals[q.id] ?? val).trim()}
              style={{ marginTop: 6, background: (textVals[q.id] ?? val).trim() ? B.goldlt : B.slate, color: (textVals[q.id] ?? val).trim() ? B.black : B.muted, border: 'none', borderRadius: 9, padding: '9px 18px', cursor: 'pointer', fontSize: 12, fontWeight: 800, fontFamily: FONT }}>
              Save answer
            </button>
          </div>
        )}
      </div>
    );
  };

  const renderCard = (q) => {
    const isDone = q.status !== 'pending';
    const isDraft = q.status === 'pending' && q.draft_answer;
    const showEditor = !locked && (!isDone || editingId === q.id);
    const currentVal = q.status === 'pending' ? q.draft_answer : q.answer;
    const child = childFor(q, textVals['sel_' + q.id] ?? currentVal);
    return (
      <div key={q.id} style={{ background: isDone ? 'rgba(76,175,125,0.07)' : B.navy, borderRadius: 14, padding: '15px 16px', marginBottom: 10, border: `1px solid ${isDone ? '#2E4A38' : isDraft ? '#2A4258' : B.slate}`, borderLeft: isDone ? '4px solid #4CAF7D' : isDraft ? '4px solid #4FA3E0' : `4px solid ${B.slate}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ color: B.white, fontSize: 15, fontWeight: 700, lineHeight: 1.4 }}>{q.item}</div>
          {isDone && <div style={{ color: '#0F0F0F', background: '#4CAF7D', fontSize: 10, fontWeight: 800, flexShrink: 0, borderRadius: 999, padding: '4px 11px' }}>✓ ANSWERED</div>}
          {isDraft && !isDone && <div style={{ color: '#4FA3E0', fontSize: 10, fontWeight: 800, flexShrink: 0, border: '1px solid #2A4258', borderRadius: 999, padding: '3px 9px' }}>Drafted</div>}
        </div>
        {q.notes && <div style={{ color: B.muted, fontSize: 12, lineHeight: 1.5, marginTop: 4 }}>{q.notes}</div>}
        {Array.isArray(q.attachments) && q.attachments.length > 0 && (
          <div style={{ marginTop: 8 }}>
            {q.attachments.map((a, ai) => {
              const isImg = /\.(png|jpe?g|gif|webp|heic)($|\?)/i.test(a.url) || /\.(png|jpe?g|gif|webp|heic)$/i.test(a.name || '');
              return isImg ? (
                <a key={ai} href={a.url} target="_blank" rel="noreferrer">
                  <img src={a.url} alt="" style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 10, marginBottom: 6, display: 'block', border: `1px solid ${B.slate}` }} />
                </a>
              ) : (
                <a key={ai} href={a.url} target="_blank" rel="noreferrer"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, background: B.mid, border: `1px solid ${B.slate}`, borderRadius: 9, padding: '9px 12px', marginBottom: 6, color: '#4FA3E0', fontSize: 12, textDecoration: 'none' }}>
                  📎 {a.name}
                </a>
              );
            })}
          </div>
        )}

        {isDone && editingId !== q.id && (
          <div style={{ marginTop: 10 }}>
            <div style={{ color: B.goldlt, fontSize: 15, fontWeight: 800 }}>{q.answer}</div>
            <div style={{ color: '#555', fontSize: 10.5, marginTop: 3 }}>{fmtStamp(q)}</div>
            {!locked && (
              <button onClick={() => { setEditingId(q.id); setTextVals(prev => ({ ...prev, ['sel_' + q.id]: q.answer, [q.id]: q.answer })); }}
                style={{ marginTop: 8, background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', color: B.muted, fontSize: 11.5, fontWeight: 700, fontFamily: FONT }}>
                ✎ Change
              </button>
            )}
          </div>
        )}

        {showEditor && (!isDone || editingId === q.id) && renderEditor(q, false)}

        {!isDone && !q.draft_answer && !locked && (
          <button onClick={() => markNA(q)}
            style={{ marginTop: 8, background: 'transparent', border: 'none', cursor: 'pointer', color: '#555', fontSize: 11, fontWeight: 700, fontFamily: FONT, padding: 0 }}>
            Not applicable to our build →
          </button>
        )}

        {child && (
          <div style={{ marginTop: 12, padding: 13, background: 'rgba(79,163,224,0.06)', border: '1px solid #2A4258', borderRadius: 12 }}>
            <div style={{ color: '#4FA3E0', fontSize: 10, letterSpacing: 2, textTransform: 'uppercase', marginBottom: 4 }}>
              ↳ Follow-up{child.status === 'pending' && !child.draft_answer ? ' — answer required' : ''}
            </div>
            <div style={{ color: B.white, fontSize: 14, fontWeight: 700, lineHeight: 1.4 }}>{child.item}</div>
            {child.status !== 'pending' && editingId !== child.id ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ color: '#4FA3E0', fontSize: 13.5, fontWeight: 800 }}>{child.answer}</div>
                <div style={{ color: '#555', fontSize: 10, marginTop: 2 }}>{fmtStamp(child)}</div>
                {!locked && (
                  <button onClick={() => { setEditingId(child.id); setTextVals(prev => ({ ...prev, ['sel_' + child.id]: child.answer, [child.id]: child.answer })); }}
                    style={{ marginTop: 6, background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 8, padding: '5px 12px', cursor: 'pointer', color: B.muted, fontSize: 11, fontWeight: 700, fontFamily: FONT }}>
                    ✎ Change
                  </button>
                )}
              </div>
            ) : (
              !locked && renderEditor(child, true)
            )}
            {child.status === 'pending' && child.draft_answer && (
              <div style={{ color: '#4FA3E0', fontSize: 10, fontWeight: 800, marginTop: 6 }}>Drafted: {child.draft_answer}</div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: B.black, zIndex: 1000, display: 'flex', flexDirection: 'column', fontFamily: FONT }}>
      <div style={{ padding: '16px 20px', borderBottom: `1px solid #1A1A1A` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <div style={{ color: B.goldlt, fontSize: 11, letterSpacing: 3, textTransform: 'uppercase' }}>📋 Pre-Build Decisions</div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {locked && <div style={{ color: '#E67E22', fontSize: 10, fontWeight: 800, border: '1px solid #5A3A1A', borderRadius: 999, padding: '3px 10px' }}>🔒 Locked</div>}
            <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: B.muted, fontSize: 22, cursor: 'pointer' }}>×</button>
          </div>
        </div>
        <div style={{ background: 'rgba(255,255,255,0.07)', borderRadius: 999, height: 6 }}>
          <div style={{ height: '100%', width: `${pct}%`, background: `linear-gradient(90deg, ${B.goldlt}, ${B.gold})`, borderRadius: 999, transition: 'width 0.3s' }} />
        </div>
        <div style={{ color: B.muted, fontSize: 10, marginTop: 5 }}>{answered.length} of {tops.length} answered</div>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {locked && (
          <div style={{ background: 'rgba(230,126,34,0.08)', border: '1px solid #5A3A1A', borderRadius: 12, padding: '12px 14px', marginBottom: 14, color: '#E67E22', fontSize: 12, lineHeight: 1.5 }}>
            🔒 These decisions are locked. Contact Dan if something needs to change.
          </div>
        )}
        {justFinished && (
          <div style={{ background: 'rgba(76,175,125,0.08)', border: '1px solid #2E4A38', borderRadius: 12, padding: '14px 16px', marginBottom: 14 }}>
            <div style={{ color: '#4CAF7D', fontSize: 14, fontWeight: 800 }}>🎉 All confirmed — Dan's been notified</div>
            <div style={{ color: B.muted, fontSize: 11.5, marginTop: 3 }}>You can still review everything here, or close with the × above.</div>
          </div>
        )}
        {cat && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
            <div style={{ color: B.goldlt, fontSize: 12, letterSpacing: 3, textTransform: 'uppercase' }}>{cat}</div>
            <div style={{ color: pageQs.every(q => q.status !== 'pending') && pageQs.length > 0 ? '#4CAF7D' : B.muted, fontSize: 11, fontWeight: 700 }}>
              {pageQs.filter(q => q.status !== 'pending').length} of {pageQs.length} answered{pageQs.every(q => q.status !== 'pending') && pageQs.length > 0 ? ' ✓' : ''}
            </div>
          </div>
        )}
        {pageQs.map(q => renderCard(q))}
        {!cat && (
          <div style={{ textAlign: 'center', paddingTop: 60 }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
            <div style={{ color: B.muted, fontSize: 13 }}>No pre-build questions yet — check back soon.</div>
          </div>
        )}
      </div>

      <div style={{ padding: '12px 20px', borderTop: `1px solid #1A1A1A` }}>
        {drafts.length > 0 && !locked && (
          <button onClick={confirmAll} disabled={saving || missingChildren > 0}
            style={{ width: '100%', marginBottom: 10, background: missingChildren > 0 ? B.slate : '#4CAF7D', color: missingChildren > 0 ? B.muted : '#fff', border: 'none', borderRadius: 12, padding: 14, cursor: 'pointer', fontSize: 14, fontWeight: 800, fontFamily: FONT }}>
            {saving ? 'Confirming…' : missingChildren > 0 ? `${missingChildren} follow-up${missingChildren !== 1 ? 's' : ''} still need answers` : `✓ Confirm ${drafts.length} Answer${drafts.length !== 1 ? 's' : ''}`}
          </button>
        )}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button onClick={() => setPageIdx(Math.max(0, pageIdx - 1))} disabled={pageIdx === 0}
            style={{ background: 'transparent', border: `1px solid ${B.slate}`, borderRadius: 12, padding: '12px 18px', cursor: pageIdx === 0 ? 'default' : 'pointer', color: pageIdx === 0 ? '#333' : B.white, fontSize: 15, fontWeight: 700, fontFamily: FONT }}>
            ←
          </button>
          <div style={{ flex: 1, textAlign: 'center', color: B.muted, fontSize: 11 }}>
            {cats.length > 0 ? `${pageIdx + 1} of ${cats.length}` : ''}
            {cats[pageIdx + 1] ? ` · Next: ${cats[pageIdx + 1]}` : ''}
          </div>
          <button onClick={() => setPageIdx(Math.min(cats.length - 1, pageIdx + 1))} disabled={pageIdx >= cats.length - 1}
            style={{ background: pageIdx >= cats.length - 1 ? 'transparent' : 'rgba(248,218,107,0.1)', border: `1px solid ${pageIdx >= cats.length - 1 ? B.slate : B.gold}`, borderRadius: 12, padding: '12px 18px', cursor: pageIdx >= cats.length - 1 ? 'default' : 'pointer', color: pageIdx >= cats.length - 1 ? '#333' : B.goldlt, fontSize: 15, fontWeight: 700, fontFamily: FONT }}>
            →
          </button>
        </div>
      </div>
    </div>
  );
}
